class NwBrandFilm extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) {
      this.mobileQuery?.addEventListener('change', this.onMediaChange);
      this.selectVideo();
      return;
    }
    this.desktopVideo = this.querySelector('.nw-film-media--desktop video');
    this.mobileVideo = this.querySelector('.nw-film-media--mobile video');
    this.videos = [this.desktopVideo, this.mobileVideo].filter(Boolean);
    if (!this.videos.length) return;
    this.dataset.ready = 'true';
    this.mobileQuery = window.matchMedia('(max-width: 767px)');
    this.onMediaChange = () => this.selectVideo();
    this.mobileQuery.addEventListener('change', this.onMediaChange);
    this.shouldPlay = this.dataset.autoplay === 'true';
    this.isMuted = true;
    this.pauseButton = this.querySelector('[data-pause]');
    this.soundButton = this.querySelector('[data-sound]');
    this.pauseButton?.addEventListener('click', () => {
      if (!this.video) return;
      this.shouldPlay = this.video.paused;
      if (this.shouldPlay) this.video.play().catch(() => this.update());
      else this.video.pause();
      this.update();
    });
    this.soundButton?.addEventListener('click', () => {
      this.isMuted = !this.isMuted;
      this.videos.forEach(video => { video.muted = this.isMuted; });
      this.update();
    });
    this.videos.forEach(video => {
      video.removeAttribute('autoplay');
      video.addEventListener('play', () => this.update());
      video.addEventListener('pause', () => this.update());
    });
    this.selectVideo();
  }
  disconnectedCallback() {
    this.mobileQuery?.removeEventListener('change', this.onMediaChange);
    this.videos?.forEach(video => video.pause());
  }
  selectVideo() {
    const active = this.mobileQuery.matches && this.mobileVideo ? this.mobileVideo : this.desktopVideo || this.mobileVideo;
    this.videos.forEach(video => {
      if (video !== active) {
        video.pause();
        video.preload = 'none';
      }
    });
    this.video = active;
    if (!active) return;
    active.muted = this.isMuted;
    active.preload = 'metadata';
    if (this.shouldPlay && active.paused) active.play().catch(() => this.update());
    this.update();
  }
  update() {
    if (this.pauseButton) {
      const paused = this.video.paused;
      this.pauseButton.setAttribute('aria-label', paused ? 'Play brand film' : 'Pause brand film');
      this.pauseButton.innerHTML = paused
        ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="m8 5 11 7-11 7V5Z"/></svg>'
        : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5v14M16 5v14"/></svg>';
    }
    if (this.soundButton) {
      const muted = this.video.muted;
      this.soundButton.setAttribute('aria-label', muted ? 'Turn sound on' : 'Mute brand film');
      this.soundButton.setAttribute('aria-pressed', String(!muted));
      this.soundButton.innerHTML = muted
        ? '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M11 5 6 9H3v6h3l5 4V5Zm5 4 5 6m0-6-5 6"/></svg>'
        : '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M11 5 6 9H3v6h3l5 4V5Zm5 3a6 6 0 0 1 0 8m3-11a10 10 0 0 1 0 14"/></svg>';
    }
  }
}
if (!customElements.get('nw-brand-film')) customElements.define('nw-brand-film', NwBrandFilm);

class NwLooks extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) {
      window.addEventListener('resize', this.handleResize, { passive: true });
      this.updateSlider();
      return;
    }
    this.dataset.ready = 'true';
    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);
    window.addEventListener('resize', this.handleResize, { passive: true });

    this.viewport = this.querySelector('[data-nw-looks-viewport]');
    this.track = this.querySelector('[data-nw-looks-track]');
    this.cards = [...this.querySelectorAll('.nw-look-card')];
    this.previousButton = this.querySelector('[data-nw-look-prev]');
    this.nextButton = this.querySelector('[data-nw-look-next]');
    this.countLabel = this.querySelector('[data-nw-look-count]');
    this.pageSize = 4;
    this.pageCount = Math.max(1, Math.ceil(this.cards.length / this.pageSize));
    this.currentPage = 0;
    this.updateSlider();

    this.querySelectorAll('.nw-look-dialog').forEach(dialog => {
      dialog.addEventListener('click', event => {
        if (event.target === dialog) dialog.close();
      });
      dialog.addEventListener('close', () => {
        dialog.querySelectorAll('[data-nw-look-product]').forEach(product => this.closeProduct(product));
        dialog.querySelectorAll('[data-nw-description-toggle]').forEach(button => this.setDescription(button, false));
        dialog.nwOpener?.focus();
      });
    });
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.handleResize);
    if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
  }

  handleClick(event) {
    const previousButton = event.target.closest('[data-nw-look-prev]');
    if (previousButton && this.contains(previousButton)) {
      this.setPage(this.currentPage - 1);
      return;
    }

    const nextButton = event.target.closest('[data-nw-look-next]');
    if (nextButton && this.contains(nextButton)) {
      this.setPage(this.currentPage + 1);
      return;
    }

    const openButton = event.target.closest('[data-open-look]');
    if (openButton && this.contains(openButton)) {
      const dialog = openButton.closest('.nw-look-card')?.querySelector('.nw-look-dialog');
      if (!dialog) return;
      dialog.nwOpener = openButton;
      const firstLook = dialog.querySelector('[data-nw-select-look]');
      if (firstLook) this.selectLook(firstLook);
      dialog.showModal();
      requestAnimationFrame(() => {
        dialog.querySelectorAll('.nw-look-dialog-layout, .nw-look-dialog-content').forEach(panel => { panel.scrollTop = 0; });
        dialog.querySelector('[data-close-look]')?.focus();
      });
      return;
    }

    const closeButton = event.target.closest('[data-close-look]');
    if (closeButton && this.contains(closeButton)) {
      closeButton.closest('.nw-look-dialog')?.close();
      return;
    }

    const lookButton = event.target.closest('[data-nw-select-look]');
    if (lookButton && this.contains(lookButton)) {
      this.selectLook(lookButton);
      return;
    }

    const descriptionButton = event.target.closest('[data-nw-description-toggle]');
    if (descriptionButton && this.contains(descriptionButton)) {
      this.setDescription(descriptionButton, descriptionButton.getAttribute('aria-expanded') !== 'true');
      return;
    }

    const toggleButton = event.target.closest('[data-nw-product-toggle]');
    if (toggleButton && this.contains(toggleButton)) {
      this.toggleProduct(toggleButton);
      return;
    }

    const variantButton = event.target.closest('[data-nw-variant]');
    if (variantButton && this.contains(variantButton)) {
      this.selectVariant(variantButton);
      return;
    }

    const addButton = event.target.closest('[data-nw-add-product]');
    if (addButton && this.contains(addButton)) this.addProduct(addButton);
  }

  handleKeydown(event) {
    const tab = event.target.closest('[data-nw-select-look]');
    if (!tab || !this.contains(tab)) return;
    const tabs = [...tab.closest('[role="tablist"]').querySelectorAll('[data-nw-select-look]')];
    const current = tabs.indexOf(tab);
    let next;
    if (event.key === 'ArrowRight') next = (current + 1) % tabs.length;
    else if (event.key === 'ArrowLeft') next = (current - 1 + tabs.length) % tabs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = tabs.length - 1;
    else return;
    event.preventDefault();
    this.selectLook(tabs[next]);
    tabs[next].focus();
  }

  selectLook(button) {
    const dialog = button.closest('.nw-look-dialog');
    if (!dialog) return;
    const target = button.getAttribute('aria-controls');
    dialog.querySelectorAll('[data-nw-select-look]').forEach(tab => {
      const selected = tab === button;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
    });
    dialog.querySelectorAll('[data-nw-look-panel]').forEach(panel => {
      panel.hidden = panel.id !== target;
      panel.scrollTop = 0;
      const content = panel.querySelector('.nw-look-dialog-content');
      if (content) content.scrollTop = 0;
      panel.querySelectorAll('[data-nw-look-product]').forEach(product => this.closeProduct(product));
      panel.querySelectorAll('[data-nw-description-toggle]').forEach(toggle => this.setDescription(toggle, false));
    });
  }

  handleResize() {
    if (this.resizeFrame) cancelAnimationFrame(this.resizeFrame);
    this.resizeFrame = requestAnimationFrame(() => {
      this.resizeFrame = 0;
      this.updateSlider();
    });
  }

  setPage(page) {
    const nextPage = Math.max(0, Math.min(page, this.pageCount - 1));
    if (nextPage === this.currentPage) return;
    this.currentPage = nextPage;
    this.updateSlider();
  }

  updateSlider() {
    if (!this.viewport || !this.track) return;
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    this.cards.forEach((card, index) => {
      if (isMobile) {
        const page = Math.floor(index / this.pageSize);
        const position = index % this.pageSize;
        card.style.gridColumn = String((page * 2) + (position % 2) + 1);
        card.style.gridRow = String(Math.floor(position / 2) + 1);
      } else {
        card.style.removeProperty('grid-column');
        card.style.removeProperty('grid-row');
      }
    });

    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    const offset = this.currentPage * (this.viewport.clientWidth + gap);
    this.track.style.transform = `translate3d(${-offset}px,0,0)`;

    const firstVisible = this.currentPage * this.pageSize;
    const lastVisible = Math.min(firstVisible + this.pageSize, this.cards.length);
    this.cards.forEach((card, index) => {
      const visible = index >= firstVisible && index < lastVisible;
      card.inert = !visible;
      card.setAttribute('aria-hidden', String(!visible));
    });

    if (this.previousButton) this.previousButton.disabled = this.currentPage === 0;
    if (this.nextButton) this.nextButton.disabled = this.currentPage >= this.pageCount - 1;
    if (this.countLabel) {
      const start = String(firstVisible + 1).padStart(2, '0');
      const end = String(lastVisible).padStart(2, '0');
      const total = String(this.cards.length).padStart(2, '0');
      this.countLabel.textContent = `${start}–${end} / ${total}`;
    }
  }

  setDescription(button, expanded) {
    const intro = button.closest('.nw-look-dialog-intro');
    if (!intro) return;
    intro.classList.toggle('is-description-expanded', expanded);
    button.setAttribute('aria-expanded', String(expanded));
    const label = button.querySelector('[data-nw-description-label]');
    if (label) label.textContent = expanded ? button.dataset.lessLabel : button.dataset.moreLabel;
  }

  toggleProduct(button) {
    const product = button.closest('[data-nw-look-product]');
    const dialog = button.closest('.nw-look-dialog');
    if (!product || !dialog) return;
    const shouldOpen = button.getAttribute('aria-expanded') !== 'true';

    dialog.querySelectorAll('[data-nw-look-product]').forEach(item => {
      if (item !== product) this.closeProduct(item);
    });

    const options = product.querySelector('[data-nw-product-options]');
    button.setAttribute('aria-expanded', String(shouldOpen));
    if (options) options.hidden = !shouldOpen;

    if (shouldOpen && product.dataset.defaultVariant) {
      product.dataset.variantId = product.dataset.defaultVariant;
      const addButton = product.querySelector('[data-nw-add-product]');
      if (addButton) addButton.disabled = false;
    }
  }

  closeProduct(product) {
    const toggle = product.querySelector('[data-nw-product-toggle]');
    const options = product.querySelector('[data-nw-product-options]');
    toggle?.setAttribute('aria-expanded', 'false');
    if (options) options.hidden = true;
  }

  selectVariant(button) {
    const product = button.closest('[data-nw-look-product]');
    if (!product) return;

    product.querySelectorAll('[data-nw-variant]').forEach(option => {
      option.setAttribute('aria-pressed', String(option === button));
    });
    product.dataset.variantId = button.dataset.variantId;

    const addButton = product.querySelector('[data-nw-add-product]');
    if (addButton) {
      addButton.disabled = false;
      addButton.textContent = addButton.dataset.defaultLabel;
      addButton.dataset.state = 'ready';
    }
    const status = product.querySelector('[data-nw-product-status]');
    if (status) status.textContent = '';
  }

  async addProduct(button) {
    const product = button.closest('[data-nw-look-product]');
    const variantId = Number(product?.dataset.variantId || product?.dataset.defaultVariant);
    if (!product || !variantId || button.dataset.state === 'loading') return;

    button.dataset.state = 'loading';
    button.disabled = true;
    button.textContent = button.dataset.addingLabel;

    try {
      const response = await fetch(this.cartAddUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] }),
      });
      if (!response.ok) throw new Error('Cart request failed');
      await response.json();

      button.dataset.state = 'added';
      button.textContent = button.dataset.addedLabel;
      const status = product.querySelector('[data-nw-product-status]');
      if (status) status.textContent = button.dataset.addedLabel;
      this.announceCartUpdate();
    } catch (error) {
      button.dataset.state = 'error';
      button.textContent = button.dataset.errorLabel;
      button.disabled = false;
      window.setTimeout(() => {
        button.dataset.state = 'ready';
        button.textContent = button.dataset.defaultLabel;
      }, 1800);
    }
  }

  cartAddUrl() {
    const root = window.Shopify?.routes?.root || '/';
    return `${root}cart/add.js`;
  }

  async announceCartUpdate() {
    const root = window.Shopify?.routes?.root || '/';
    let cart = null;

    try {
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      if (response.ok) cart = await response.json();
    } catch (error) {
      cart = null;
    }

    document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
      bubbles: true,
      detail: { open: false },
    }));

    if (!cart) return;

    document.dispatchEvent(new CustomEvent('cart:updated', {
      bubbles: true,
      detail: { source: 'nw-winter-looks', cart },
    }));

    const pubsub = window.FoxTheme?.pubsub;
    const cartUpdateEvent = pubsub?.PUB_SUB_EVENTS?.cartUpdate;
    if (pubsub?.publish && cartUpdateEvent) {
      pubsub.publish(cartUpdateEvent, { source: 'nw-winter-looks', cart });
    }
  }
}
if (!customElements.get('nw-looks')) customElements.define('nw-looks', NwLooks);


class NwAdaptiveHeader {
  constructor() {
    this.section = document.querySelector('body.aw26-winter .header-section');
    this.canvas = document.createElement('canvas');
    this.canvas.width = 1;
    this.canvas.height = 1;
    this.context = this.canvas.getContext('2d', { willReadFrequently: true });
    this.frame = 0;
    this.videoTimer = 0;
    this.boundSchedule = () => this.schedule();
  }

  start() {
    if (!this.section || !this.context) return;
    window.addEventListener('scroll', this.boundSchedule, { passive: true });
    window.addEventListener('resize', this.boundSchedule, { passive: true });
    document.addEventListener('visibilitychange', this.boundSchedule);
    document.querySelectorAll('.nw-hero video').forEach(video => {
      ['loadeddata', 'play', 'pause', 'seeked'].forEach(eventName => video.addEventListener(eventName, this.boundSchedule));
    });
    this.videoTimer = window.setInterval(() => {
      if (!document.hidden && this.isHeroBehindHeader()) this.schedule();
    }, 220);
    this.schedule();
  }

  stop() {
    window.removeEventListener('scroll', this.boundSchedule);
    window.removeEventListener('resize', this.boundSchedule);
    document.removeEventListener('visibilitychange', this.boundSchedule);
    window.clearInterval(this.videoTimer);
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  schedule() {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.update();
    });
  }

  update() {
    const controls = [...this.section.querySelectorAll('.header__icons a, .header__icons button')]
      .filter(control => {
        const rect = control.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      });
    if (!controls.length) return;

    /* Use one shared reading so every header icon changes at the same time. */
    controls.forEach(control => {
      control.style.removeProperty('--nw-header-icon-color');
      delete control.dataset.nwIconTone;
    });
    const headerRect = this.section.getBoundingClientRect();
    const sampleX = [.04, .16, .28, .4, .6, .72, .84, .96];
    const sampleY = [.35, .5, .65];
    const values = [];
    sampleX.forEach(xRatio => sampleY.forEach(yRatio => {
      const value = this.luminanceAt(
        window.innerWidth * xRatio,
        headerRect.top + headerRect.height * yRatio
      );
      if (Number.isFinite(value)) values.push(value);
    }));
    if (!values.length) return;

    values.sort((a, b) => a - b);
    const luminance = values[Math.floor(values.length / 2)];
    const wasDark = this.section.dataset.nwIconTone === 'dark';
    const useDark = wasDark ? luminance > .14 : luminance > .23;
    this.section.dataset.nwIconTone = useDark ? 'dark' : 'light';
    this.section.style.setProperty('--nw-header-icon-color', useDark ? '#111111' : '#ffffff');
  }

  isHeroBehindHeader() {
    const hero = document.querySelector('.nw-hero');
    if (!hero) return false;
    const heroRect = hero.getBoundingClientRect();
    const headerRect = this.section.getBoundingClientRect();
    return heroRect.top < headerRect.bottom && heroRect.bottom > headerRect.top;
  }

  luminanceAt(x, y) {
    const stack = document.elementsFromPoint(x, y);
    for (const element of stack) {
      if (this.section.contains(element)) continue;
      if (element.matches('.signup-overlay') || element.closest('#verifast-bot-root, [id*="verifast"]')) continue;

      if (element instanceof HTMLImageElement || element instanceof HTMLVideoElement) {
        const pixel = this.mediaPixel(element, x, y);
        if (pixel) return this.relativeLuminance(pixel[0], pixel[1], pixel[2]);
      }

      const color = this.opaqueBackground(element);
      if (color) return this.relativeLuminance(color[0], color[1], color[2]);
    }
    return 0;
  }

  mediaPixel(media, x, y) {
    const rect = media.getBoundingClientRect();
    const sourceWidth = media instanceof HTMLVideoElement ? media.videoWidth : media.naturalWidth;
    const sourceHeight = media instanceof HTMLVideoElement ? media.videoHeight : media.naturalHeight;
    if (!sourceWidth || !sourceHeight || !rect.width || !rect.height) return null;

    const style = getComputedStyle(media);
    const fit = style.objectFit || 'fill';
    let scaleX = rect.width / sourceWidth;
    let scaleY = rect.height / sourceHeight;
    if (fit === 'cover' || fit === 'contain' || fit === 'scale-down') {
      const scale = fit === 'cover' ? Math.max(scaleX, scaleY) : Math.min(scaleX, scaleY);
      scaleX = scale;
      scaleY = scale;
    }

    const drawnWidth = sourceWidth * scaleX;
    const drawnHeight = sourceHeight * scaleY;
    const position = (style.objectPosition || '50% 50%').trim().split(/\s+/);
    const offsetX = this.objectOffset(position[0] || '50%', rect.width - drawnWidth, 'x');
    const offsetY = this.objectOffset(position[1] || position[0] || '50%', rect.height - drawnHeight, 'y');
    const sourceX = (x - rect.left - offsetX) / scaleX;
    const sourceY = (y - rect.top - offsetY) / scaleY;

    if (sourceX < 0 || sourceY < 0 || sourceX >= sourceWidth || sourceY >= sourceHeight) return null;

    try {
      this.context.clearRect(0, 0, 1, 1);
      this.context.drawImage(media, sourceX, sourceY, 1, 1, 0, 0, 1, 1);
      return this.context.getImageData(0, 0, 1, 1).data;
    } catch (error) {
      return null;
    }
  }

  objectOffset(value, freeSpace, axis) {
    if (value.endsWith('%')) return freeSpace * (parseFloat(value) || 0) / 100;
    if (value.endsWith('px')) return parseFloat(value) || 0;
    const keyword = value.toLowerCase();
    if (keyword === 'center') return freeSpace * .5;
    if ((axis === 'x' && keyword === 'right') || (axis === 'y' && keyword === 'bottom')) return freeSpace;
    return 0;
  }

  opaqueBackground(element) {
    const match = getComputedStyle(element).backgroundColor.match(/[\d.]+/g);
    if (!match || match.length < 3) return null;
    const alpha = match.length > 3 ? Number(match[3]) : 1;
    return alpha >= .85 ? match.slice(0, 3).map(Number) : null;
  }

  relativeLuminance(red, green, blue) {
    const channel = value => {
      const normalized = value / 255;
      return normalized <= .04045 ? normalized / 12.92 : ((normalized + .055) / 1.055) ** 2.4;
    };
    return .2126 * channel(red) + .7152 * channel(green) + .0722 * channel(blue);
  }
}

const startNwAdaptiveHeader = () => {
  if (!document.body.classList.contains('aw26-winter')) return;
  window.nwAdaptiveHeader?.stop();
  window.nwAdaptiveHeader = new NwAdaptiveHeader();
  window.nwAdaptiveHeader.start();
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', startNwAdaptiveHeader, { once: true });
} else {
  startNwAdaptiveHeader();
}

document.addEventListener('shopify:section:load', event => {
  event.target.querySelectorAll('nw-brand-film,nw-looks').forEach(element => element.connectedCallback());
  startNwAdaptiveHeader();
});
